import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Upload, Image, Video, Mic, FileText, Trash2, X, Search,
  Play, Pause, Download, ChevronLeft, ChevronRight, Volume2, ShieldCheck
} from "lucide-react";
import { pageTransition, fadeUp } from "@/lib/animations";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import VoiceRecorder from "@/components/VoiceRecorder";
import VideoRecorder from "@/components/VideoRecorder";
import api from "@/lib/api";
import { toast } from "sonner";

const typeIcons = { photo: Image, video: Video, voice_note: Mic, document: FileText };

export default function VaultPage() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [previewEntry, setPreviewEntry] = useState(null);
  const [recordingVoice, setRecordingVoice] = useState(false);
  const [uploadForm, setUploadForm] = useState({ title: "", description: "", entry_type: "photo", file: null });
  const [voiceTitle, setVoiceTitle] = useState("");
  const [recordingVideo, setRecordingVideo] = useState(false);
  const [videoTitle, setVideoTitle] = useState("");
  const fileInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const loadEntries = async () => {
    try {
      const res = await api.get("/vault/entries", { params: { entry_type: filter === "all" ? undefined : filter } });
      setEntries(res.data.entries);
      setTotal(res.data.total);
    } catch {
      toast.error("Failed to load vault entries");
    }
  };

  useEffect(() => { loadEntries(); }, [filter]);

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults(null); return; }
    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await api.get("/search", { params: { q: searchQuery, content_type: "vault" } });
        setSearchResults(res.data.results);
      } catch { /* ignore */ }
    }, 400);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchQuery]);

  const handleUpload = async () => {
    if (!uploadForm.file) { toast.error("Please select a file"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadForm.file);
      formData.append("title", uploadForm.title || uploadForm.file.name);
      formData.append("description", uploadForm.description);
      formData.append("entry_type", uploadForm.entry_type);
      await api.post("/vault/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded to vault!");
      setShowUpload(false);
      setUploadForm({ title: "", description: "", entry_type: "photo", file: null });
      loadEntries();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleVideoRecording = async (blob) => {
    setUploading(true);
    try {
      // Determine file extension based on mime type
      const mimeType = blob.type || "video/webm";
      const ext = mimeType.includes("mp4") ? "mp4" : "webm";
      const formData = new FormData();
      formData.append("video", blob, `recording.${ext}`);
      formData.append("title", videoTitle || "Video Recording");
      formData.append("description", "");
      formData.append("content_type", mimeType);
      await api.post("/vault/record-video", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Video saved to vault!");
      setRecordingVideo(false);
      setVideoTitle("");
      loadEntries();
    } catch (err) {
      console.error("Video save error:", err);
      toast.error("Failed to save video");
    } finally {
      setUploading(false);
    }
  };

  const handleVoiceRecording = async (blob) => {
    setUploading(true);
    try {
      // Determine file extension based on mime type
      const mimeType = blob.type || "audio/webm";
      const ext = mimeType.includes("mp4") ? "m4a" : "webm";
      const formData = new FormData();
      formData.append("audio", blob, `voice_note.${ext}`);
      formData.append("title", voiceTitle || "Voice Note");
      formData.append("description", "");
      formData.append("content_type", mimeType);
      await api.post("/vault/record-voice", formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Voice note saved and transcribed!");
      setRecordingVoice(false);
      setVoiceTitle("");
      loadEntries();
    } catch (err) {
      console.error("Voice save error:", err);
      toast.error("Failed to save voice note");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm("Delete this memory?")) return;
    try {
      await api.delete(`/vault/entries/${entryId}`);
      toast.success("Entry deleted");
      setPreviewEntry(null);
      loadEntries();
    } catch {
      toast.error("Delete failed");
    }
  };

  const displayEntries = searchResults || entries;
  const filters = [
    { value: "all", label: "All" },
    { value: "photo", label: "Photos" },
    { value: "video", label: "Videos" },
    { value: "voice_note", label: "Voice" },
    { value: "document", label: "Docs" },
  ];

  // Navigate preview
  const currentIndex = previewEntry ? entries.findIndex(e => e.entry_id === previewEntry.entry_id) : -1;
  const navigatePreview = (dir) => {
    const next = currentIndex + dir;
    if (next >= 0 && next < entries.length) setPreviewEntry(entries[next]);
  };

  return (
    <motion.div variants={pageTransition} initial="hidden" animate="visible" className="p-6 md:p-12 max-w-6xl mx-auto" data-testid="vault-page">
      {/* Header */}
      <motion.div variants={fadeUp} className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#00FF88] mb-2 font-medium">Memory Vault</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Your Vault</h1>
          <p className="text-sm text-slate-400 mt-1">{total} memories stored</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setRecordingVideo(true)}
            data-testid="vault-record-video-btn"
            className="bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 rounded-full px-5 font-medium"
          >
            <Video className="w-4 h-4 mr-2" /> Video
          </Button>
          <Button
            onClick={() => setRecordingVoice(true)}
            data-testid="vault-record-voice-btn"
            className="bg-[#00FF88]/10 border border-[#00FF88]/20 text-[#00FF88] hover:bg-[#00FF88]/20 rounded-full px-5 font-medium"
          >
            <Mic className="w-4 h-4 mr-2" /> Voice
          </Button>
          <Button
            onClick={() => setShowUpload(true)}
            data-testid="vault-upload-btn"
            className="bg-white text-black hover:bg-slate-200 rounded-full px-6 font-medium"
          >
            <Upload className="w-4 h-4 mr-2" /> Upload
          </Button>
        </div>
      </motion.div>
      {/* Search Bar */}
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input
          placeholder="Search your memories..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          data-testid="vault-search-input"
          className="bg-white/5 border-white/10 text-white placeholder:text-white/20 rounded-xl h-12 pl-11"
        />
        {searchQuery && (
          <button
            onClick={() => { setSearchQuery(""); setSearchResults(null); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Filters */}
      {!searchQuery && (
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2 glass-card rounded-xl p-1.5 w-fit">
          {filters.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              data-testid={`filter-${f.value}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                filter === f.value ? "bg-white/10 text-white" : "text-slate-500 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {searchResults !== null && (
        <p className="text-xs text-slate-500 mb-4">{searchResults.length} results for "{searchQuery}"</p>
      )}

      {/* Grid */}
      {displayEntries.length === 0 ? (
        <div className="text-center py-24">
          <ArchiveIcon className="w-12 h-12 text-slate-700 mx-auto mb-4" />
          <p className="text-slate-500 text-lg">{searchQuery ? "No results found" : "Your vault is empty"}</p>
          <p className="text-slate-600 text-sm mt-2">{searchQuery ? "Try a different search" : "Upload your first memory to get started"}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {displayEntries.map((entry, i) => {
            const Icon = typeIcons[entry.entry_type] || FileText;
            return (
              <motion.div
                key={entry.entry_id}
                initial={{ opacity: 0, y: 15, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: i * 0.04, type: "spring", stiffness: 300, damping: 25 }}
                whileHover={{ scale: 1.03, y: -4 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setPreviewEntry(entry)}
                className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-[20px] overflow-hidden group hover:border-white/[0.15] transition-colors duration-300 relative cursor-pointer shadow-[0_8px_32px_rgba(0,0,0,0.3)]"
                data-testid={`vault-entry-${entry.entry_id}`}
              >
                {entry.content_type?.startsWith("image") ? (
                  <img src={`${BACKEND_URL}/api/vault/files/${entry.file_id}`} alt={entry.title} className="w-full h-40 object-cover" loading="lazy" />
                ) : entry.content_type?.startsWith("video") ? (
                  <div className="w-full h-40 bg-white/5 flex items-center justify-center relative">
                    <video src={`${BACKEND_URL}/api/vault/files/${entry.file_id}`} className="w-full h-40 object-cover" />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <Play className="w-10 h-10 text-white/80" fill="currentColor" />
                    </div>
                  </div>
                ) : entry.entry_type === "voice_note" ? (
                  <div className="w-full h-40 bg-gradient-to-br from-emerald-500/5 to-transparent flex flex-col items-center justify-center gap-2">
                    <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
                      <Volume2 className="w-5 h-5 text-emerald-400" />
                    </div>
                    {entry.transcription && (
                      <p className="text-[10px] text-slate-500 px-4 text-center line-clamp-2">{entry.transcription}</p>
                    )}
                  </div>
                ) : (
                  <div className="w-full h-40 bg-white/5 flex items-center justify-center">
                    <Icon className="w-10 h-10 text-slate-700" strokeWidth={1.5} />
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-white truncate flex-1">{entry.title}</p>
                    {entry.verified_moment && (
                      <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0" title="Verified Moment - Captured in-app">
                        <ShieldCheck className="w-3 h-3 text-emerald-400" />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(entry.entry_id); }}
                  data-testid={`delete-entry-${entry.entry_id}`}
                  className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-500/80"
                >
                  <Trash2 className="w-3 h-3 text-white" />
                </button>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Preview Modal */}
      <AnimatePresence>
        {previewEntry && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
            data-testid="vault-preview-modal"
            onClick={() => setPreviewEntry(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative max-w-4xl w-full max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close */}
              <button
                onClick={() => setPreviewEntry(null)}
                data-testid="preview-close-btn"
                className="absolute -top-12 right-0 text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Navigation arrows */}
              {currentIndex > 0 && (
                <button
                  onClick={() => navigatePreview(-1)}
                  data-testid="preview-prev-btn"
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-14 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}
              {currentIndex < entries.length - 1 && (
                <button
                  onClick={() => navigatePreview(1)}
                  data-testid="preview-next-btn"
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-14 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}

              {/* Media */}
              <div className="rounded-[20px] overflow-hidden bg-white/[0.03] backdrop-blur-xl border border-white/[0.08]">
                {previewEntry.content_type?.startsWith("image") ? (
                  <img
                    src={`${BACKEND_URL}/api/vault/files/${previewEntry.file_id}`}
                    alt={previewEntry.title}
                    className="w-full max-h-[60vh] object-contain bg-black"
                  />
                ) : previewEntry.content_type?.startsWith("video") ? (
                  <video
                    src={`${BACKEND_URL}/api/vault/files/${previewEntry.file_id}`}
                    controls
                    autoPlay
                    className="w-full max-h-[60vh] bg-black"
                    data-testid="preview-video-player"
                  />
                ) : previewEntry.content_type?.startsWith("audio") || previewEntry.entry_type === "voice_note" ? (
                  <div className="p-12 flex flex-col items-center gap-6">
                    <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center">
                      <Volume2 className="w-8 h-8 text-emerald-400" />
                    </div>
                    <audio
                      src={`${BACKEND_URL}/api/vault/files/${previewEntry.file_id}`}
                      controls
                      className="w-full max-w-md"
                      data-testid="preview-audio-player"
                    />
                    {previewEntry.transcription && (
                      <div className="bg-white/5 rounded-xl p-4 max-w-lg w-full">
                        <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">Transcription</p>
                        <p className="text-sm text-slate-300 leading-relaxed">{previewEntry.transcription}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-12 flex items-center justify-center">
                    <FileText className="w-16 h-16 text-slate-600" />
                  </div>
                )}

                {/* Info bar */}
                <div className="p-6 border-t border-white/[0.06]">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-white" style={{ fontFamily: 'Manrope' }}>{previewEntry.title}</h3>
                        {previewEntry.verified_moment && (
                          <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                            <ShieldCheck className="w-3 h-3 text-emerald-400" />
                            <span className="text-[10px] font-medium text-emerald-400">Verified Moment</span>
                          </div>
                        )}
                      </div>
                      {previewEntry.description && (
                        <p className="text-sm text-slate-400 mt-1 max-w-lg">{previewEntry.description}</p>
                      )}
                      <p className="text-xs text-slate-600 mt-2">
                        {new Date(previewEntry.created_at).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                        {previewEntry.file_size && <span className="ml-2">{(previewEntry.file_size / 1024 / 1024).toFixed(1)} MB</span>}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <a
                        href={`${BACKEND_URL}/api/vault/files/${previewEntry.file_id}`}
                        download={previewEntry.filename}
                        data-testid="preview-download-btn"
                        className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                      <button
                        onClick={() => handleDelete(previewEntry.entry_id)}
                        data-testid="preview-delete-btn"
                        className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="bg-[#111]/95 backdrop-blur-2xl border-white/[0.1] text-white max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Upload to Vault</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Select value={uploadForm.entry_type} onValueChange={(v) => setUploadForm({ ...uploadForm, entry_type: v })}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-xl h-12" data-testid="upload-type-select">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent className="bg-[#0F0F10] border-white/10 text-white">
                <SelectItem value="photo">Photo</SelectItem>
                <SelectItem value="video">Video</SelectItem>
                <SelectItem value="voice_note">Voice Note</SelectItem>
                <SelectItem value="document">Document</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Title" value={uploadForm.title} onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })} data-testid="upload-title-input" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl h-12" />
            <Input placeholder="Description (optional)" value={uploadForm.description} onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })} data-testid="upload-description-input" className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl h-12" />
            <div onClick={() => fileInputRef.current?.click()} className="border-2 border-dashed border-white/10 rounded-xl p-8 text-center cursor-pointer hover:border-white/20 transition-colors duration-200" data-testid="upload-dropzone">
              <Upload className="w-8 h-8 text-slate-600 mx-auto mb-3" />
              <p className="text-sm text-slate-400">{uploadForm.file ? uploadForm.file.name : "Click to select a file"}</p>
              <input 
                ref={fileInputRef} 
                type="file" 
                className="hidden" 
                accept={
                  uploadForm.entry_type === "photo" ? "image/*" :
                  uploadForm.entry_type === "video" ? "video/*" :
                  uploadForm.entry_type === "voice_note" ? "audio/*" :
                  "*/*"
                }
                onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })} 
                data-testid="upload-file-input" 
              />
            </div>
            <Button onClick={handleUpload} disabled={uploading || !uploadForm.file} data-testid="upload-submit-btn" className="w-full bg-white text-black hover:bg-slate-200 rounded-full h-12 font-medium">
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Voice Recording Dialog */}
      <Dialog open={recordingVoice} onOpenChange={setRecordingVoice}>
        <DialogContent className="bg-[#111]/95 backdrop-blur-2xl border-white/[0.1] text-white max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Record Voice Note</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            <Input
              placeholder="Title for your voice note"
              value={voiceTitle}
              onChange={(e) => setVoiceTitle(e.target.value)}
              data-testid="voice-title-input"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl h-12"
            />
            <div className="flex flex-col items-center gap-4 py-6">
              <p className="text-sm text-slate-400">Press to record, press again to stop and save</p>
              <VoiceRecorder onRecordingComplete={handleVoiceRecording} disabled={uploading} />
              {uploading && (
                <p className="text-sm text-[#00FF88] animate-pulse">Saving & transcribing...</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Video Recording Dialog */}
      <Dialog open={recordingVideo} onOpenChange={setRecordingVideo}>
        <DialogContent className="bg-[#111]/95 backdrop-blur-2xl border-white/[0.1] text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Record Video</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Input
              placeholder="Title for your video"
              value={videoTitle}
              onChange={(e) => setVideoTitle(e.target.value)}
              data-testid="video-title-input"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl h-12"
            />
            <VideoRecorder onRecordingComplete={handleVideoRecording} disabled={uploading} />
            {uploading && (
              <p className="text-sm text-[#00FF88] animate-pulse text-center">Saving video to vault...</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}

function ArchiveIcon(props) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect width="20" height="5" x="2" y="3" rx="1" /><path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" /><path d="M10 12h4" />
    </svg>
  );
}
